import { useState, useEffect } from "react";
import finacraft_api from "../../api_adapters/finacraft_api";
import currencyCodes from "currency-codes";
import { useOnboarding } from "../context/OnboardingContext";

export default function IncomeSourceStage({ clientId, onComplete, onPrevious }) {
  const {
    state,
    createIncome,
    updateIncome,
    deleteIncome,
    bulkSaveIncomes,
    setStep,
    getBankAccounts,
    getIncomes,
  } = useOnboarding();

  const incomes = getIncomes();
  const bankAccounts = getBankAccounts();
  const selectedCurrency = state.currency || "INR";
  const [currentIncome, setCurrentIncome] = useState({
    name: "",
    amount: "",
    frequency: "",
    type: "",
    bank_account: "",
    id: null, // null for new, set to id when editing
  });
  const [isAdding, setIsAdding] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [incomeFrequencies, setIncomeFrequencies] = useState([]);
  const [incomeTypes, setIncomeTypes] = useState([]);

  // Get currency symbol
  const getCurrencySymbol = () => {
    try {
      const currency = currencyCodes.code(selectedCurrency);
      return currency?.symbol || selectedCurrency;
    } catch (e) {
      return "₹";
    }
  };

  // Load reference data (frequencies and types)
  useEffect(() => {
    if (clientId) {
      loadIncomeFrequencies();
      loadIncomeTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const loadIncomeFrequencies = async () => {
    try {
      const response = await finacraft_api.get(`/core_api/income-frequencies/`);
      setIncomeFrequencies(response.data || []);
    } catch (error) {
      console.error("Error loading income frequencies:", error);
    }
  };

  const loadIncomeTypes = async () => {
    try {
      const response = await finacraft_api.get(`/core_api/income-types/`);
      setIncomeTypes(response.data || []);
    } catch (error) {
      console.error("Error loading income types:", error);
    }
  };


  const validateIncome = (income) => {
    const newErrors = {};

    if (!income.name) {
      newErrors.name = "Income name is required";
    }

    if (!income.amount || income.amount === "") {
      newErrors.amount = "Amount is required";
    } else if (isNaN(income.amount) || parseFloat(income.amount) <= 0) {
      newErrors.amount = "Amount must be a valid positive number";
    }

    if (!income.frequency) {
      newErrors.frequency = "Frequency is required";
    }

    if (!income.type) {
      newErrors.type = "Income type is required";
    }

    if (!income.bank_account) {
      newErrors.bank_account = "Bank account is required";
    }

    if (
      incomes.filter((existing_income) => {
        return existing_income.name == income.name.trim();
      }).length >= 1
    ) {
      newErrors.name = "Income with this name already exists";
    }

    return newErrors;
  };

  const handleAddIncome = async () => {
    const validationErrors = validateIncome(currentIncome);

    if (Object.keys(validationErrors).length === 0) {
      if (currentIncome.id) {
        // Editing existing income
        try {
          await updateIncome(currentIncome.id, {
            name: currentIncome.name.trim(),
            amount: currentIncome.amount,
            frequency: currentIncome.frequency,
            type: currentIncome.type,
            bank_account: currentIncome.bank_account,
          });
          setCurrentIncome({
            name: "",
            amount: "",
            frequency: "",
            type: "",
            bank_account: "",
            id: null,
          });
          setErrors({});
          setIsAdding(false);
        } catch (error) {
          // Error already handled in Context
        }
      } else {
        // Creating new income
        createIncome({
          name: currentIncome.name.trim(),
          amount: currentIncome.amount,
          frequency: currentIncome.frequency,
          type: currentIncome.type,
          bank_account: currentIncome.bank_account,
        });
        setCurrentIncome({
          name: "",
          amount: "",
          frequency: "",
          type: "",
          bank_account: "",
          id: null,
        });
        setErrors({});
        setIsAdding(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  const handleRemoveIncome = async (income) => {
    await deleteIncome(income.id);
  };

  const handleEditIncome = (income) => {
    setCurrentIncome({
      name: income.name?.toString() || "",
      amount: income.amount?.toString() || "",
      frequency: income.frequency?.toString() || income.frequency_detail?.id?.toString() || "",
      type: income.type?.toString() || income.type_detail?.id?.toString() || "",
      bank_account: income.bank_account?.toString() || "",
      id: income.id,
    });
    setIsAdding(true);
  };

  const handleSubmit = async () => {
    if (incomes.length === 0) {
      alert("Please add at least one income source.");
      return;
    }

    setIsSubmitting(true);

    try {
      await bulkSaveIncomes();
      onComplete();
    } catch (error) {
      // Error already handled in Context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrevious = () => {
    setStep(1);
    if (onPrevious) onPrevious();
  };

  const getIncomeFrequencyName = (frequencyId) => {
    if (!frequencyId) return "Unknown";
    const frequency = incomeFrequencies.find((f) => f.id === frequencyId || f.id === parseInt(frequencyId));
    return frequency ? frequency.name : `Frequency ${frequencyId}`;
  };

  const getIncomeTypeName = (typeId) => {
    if (!typeId) return "Unknown";
    const type = incomeTypes.find((t) => t.id === typeId || t.id === parseInt(typeId));
    return type ? type.name : `Type ${typeId}`;
  };

  const getBankAccountName = (accountId) => {
    if (!accountId) return "Unknown";
    const account = bankAccounts.find((a) => a.id === accountId || a.id === parseInt(accountId));
    return account ? account.name : `Account ${accountId}`;
  };

  return (
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">Step 2: Add Income Sources</h5>
                <small className="text-muted">
                  Add one or more income sources to track your earnings
                </small>
              </div>
            </div>
            <div className="card-body">
              {/* Existing Incomes List */}
              {incomes.length > 0 && (
                <div className="mb-4">
                  <h6>Your Income Sources ({incomes.length})</h6>
                  <div className="list-group">
                    {incomes.map((income, index) => (
                      <div
                        key={index}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <strong>{income.name}</strong>{" "}
                          {income.created_at ? (
                            <span className="badge bg-success">Saved</span>
                          ) : (
                            <span className="badge bg-primary">New</span>
                          )}
                          <br />
                          <small className="text-muted">
                            Amount: {getCurrencySymbol()}
                            {income.amount?.toLocaleString() || income.amount}{" "}
                            ({income.frequency_detail?.name ||
                              getIncomeFrequencyName(income.frequency)})
                            <br />
                            Type:{" "}
                            {income.type_detail?.name ||
                              getIncomeTypeName(income.type)}
                            {" • "}
                            Account:{" "}
                            {getBankAccountName(income.bank_account)}
                          </small>
                        </div>
                        <div>
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEditIncome(income)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleRemoveIncome(income)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add New Income Form */}
              {isAdding ? (
                <div className="card mb-3">
                  <div className="card-body">
                    <h6>{currentIncome.id ? "Edit Income Source" : "Add New Income Source"}</h6>
                    <div className="mb-3">
                      <label className="form-label">Income Name *</label>
                      <input
                        type="text"
                        className={`form-control ${
                          errors.name ? "is-invalid" : ""
                        }`}
                        placeholder="e.g., Company Salary, Freelance Work, etc."
                        value={currentIncome.name}
                        onChange={(e) =>
                          setCurrentIncome({
                              ...currentIncome,
                              name: e.target.value,
                            })
                        }
                      />
                      {errors.name && (
                        <div className="invalid-feedback">{errors.name}</div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Amount *</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          {getCurrencySymbol()}
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className={`form-control ${
                            errors.amount ? "is-invalid" : ""
                          }`}
                          placeholder="0.00"
                          value={currentIncome.amount}
                          onChange={(e) =>
                            setCurrentIncome({
                              ...currentIncome,
                              amount: e.target.value,
                            })
                          }
                        />
                        {errors.amount && (
                          <div className="invalid-feedback">
                            {errors.amount}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Frequency *</label>
                      <select
                        className={`form-control ${
                          errors.frequency ? "is-invalid" : ""
                        }`}
                        value={currentIncome.frequency}
                        onChange={(e) =>
                          setCurrentIncome({
                            ...currentIncome,
                            frequency: e.target.value,
                          })
                        }
                      >
                        <option value="">Select frequency</option>
                        {incomeFrequencies.map((frequency) => (
                          <option key={frequency.id} value={frequency.id}>
                            {frequency.name}
                          </option>
                        ))}
                      </select>
                      {errors.frequency && (
                        <div className="invalid-feedback">
                          {errors.frequency}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Income Type *</label>
                      <select
                        className={`form-control ${
                          errors.type ? "is-invalid" : ""
                        }`}
                        value={currentIncome.type}
                        onChange={(e) =>
                          setCurrentIncome({
                            ...currentIncome,
                            type: e.target.value,
                          })
                        }
                      >
                        <option value="">Select income type</option>
                        {incomeTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                      {errors.type && (
                        <div className="invalid-feedback">{errors.type}</div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Bank Account *</label>
                      <select
                        className={`form-control ${
                          errors.bank_account ? "is-invalid" : ""
                        }`}
                        value={currentIncome.bank_account}
                        onChange={(e) =>
                          setCurrentIncome({
                            ...currentIncome,
                            bank_account: e.target.value,
                          })
                        }
                      >
                        <option value="">Select bank account</option>
                        {bankAccounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.name} - {getCurrencySymbol()}
                            {account.balance?.toLocaleString() || account.balance}
                          </option>
                        ))}
                      </select>
                      {errors.bank_account && (
                        <div className="invalid-feedback">
                          {errors.bank_account}
                        </div>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-primary"
                        onClick={handleAddIncome}
                      >
                        {currentIncome.id ? "Update Income Source" : "Add Income Source"}
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          setIsAdding(false);
                          setCurrentIncome({
                            name: "",
                            amount: "",
                            frequency: "",
                            type: "",
                            bank_account: "",
                            id: null,
                          });
                          setErrors({});
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  className="btn btn-outline-primary mb-3"
                  onClick={() => setIsAdding(true)}
                >
                  + Add Income Source
                </button>
              )}

              {/* Action Buttons */}
              <div className="d-flex justify-content-between">
                <button
                  className="btn btn-secondary"
                  onClick={handlePrevious}
                >
                  Previous
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting || incomes.length === 0}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Saving...
                    </>
                  ) : (
                    `Continue with ${incomes.length} income source${
                      incomes.length !== 1 ? "s" : ""
                    }`
                  )}
                </button>
              </div>

              {/* Progress Indicator */}
              <div className="mt-3">
                <div className="progress" style={{ height: "4px" }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: "66%" }}
                    aria-valuenow="66"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
                <small className="text-muted">Step 2 of 3</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
